import React, { createContext, useContext, useState, useCallback } from 'react';

interface PrescriptionContextType {
  prescriptionInCart: boolean;
  orderValidated: boolean;
  setPrescriptionInCart: (value: boolean) => void;
  validateOrder: () => void;
  getPrescriptionPdfAsBase64: () => Promise<string | null>;
}

const PrescriptionContext = createContext<PrescriptionContextType | undefined>(undefined);

export const PrescriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [prescriptionInCart, setPrescriptionInCart] = useState<boolean>(false);
  const [orderValidated, setOrderValidated] = useState<boolean>(false);

  const validateOrder = useCallback(() => {
    // Simulate a delay as if processing the order
    setTimeout(() => {
      setOrderValidated(true);
    }, 2000);
  }, []);

  // Function to get the prescription PDF as base64 string
  const getPrescriptionPdfAsBase64 = useCallback(async (): Promise<string | null> => {
    // Check if there's a prescription in the cart and the order is validated
    if (!prescriptionInCart || !orderValidated) {
      console.log('Cannot generate prescription PDF: No prescription in cart or order not validated');
      return null;
    }

    try {
      // Try to access the RecommendationResults component's function
      const RecommendationResults = (window as any).RecommendationResults;
      if (RecommendationResults && typeof RecommendationResults.generatePrescriptionPdfAsBase64 === 'function') {
        return await RecommendationResults.generatePrescriptionPdfAsBase64();
      } else {
        console.error('generatePrescriptionPdfAsBase64 function not found');
        return null;
      }
    } catch (error) {
      console.error('Error generating prescription PDF:', error);
      return null;
    }
  }, [prescriptionInCart, orderValidated]);

  return (
    <PrescriptionContext.Provider
      value={{
        prescriptionInCart,
        orderValidated,
        setPrescriptionInCart,
        validateOrder,
        getPrescriptionPdfAsBase64
      }}
    >
      {children}
    </PrescriptionContext.Provider>
  );
};

export const usePrescription = () => {
  const context = useContext(PrescriptionContext);
  if (context === undefined) {
    throw new Error('usePrescription must be used within a PrescriptionProvider');
  }
  return context;
}; 